/**
 * Minimal, dependency-free i18n runtime for the vanilla-TS frontends.
 * - Dot-path key lookup against a locale dictionary
 * - {placeholder} interpolation
 * - Locale switching with change subscribers
 * Business content is NOT here; only UI chrome. CMS strings arrive via GraphQL.
 */
import type { Locale } from "@mb-group/shared-types";
import { DEFAULT_LOCALE, LOCALES } from "@mb-group/shared-types";
import { pl } from "./locales/pl.js";
import { en } from "./locales/en.js";
import type { Dictionary } from "./types.js";

const DICTIONARIES: Record<Locale, Dictionary> = { pl, en };

/** Dot-separated leaf paths, e.g. "nav.services". */
type Leaves<T, P extends string = ""> = {
	[K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

/** Every valid dot-path into the dictionary (e.g. `"nav.services"`), typed from `pl`. */
export type MessageKey = Leaves<Dictionary>;

function resolve(dict: Dictionary, key: string): string | undefined {
	return key.split(".").reduce<unknown>((acc, part) => {
		if (acc && typeof acc === "object" && part in (acc as Record<string, unknown>)) {
			return (acc as Record<string, unknown>)[part];
		}
		return undefined;
	}, dict) as string | undefined;
}

function interpolate(template: string, params?: Record<string, string | number>): string {
	if (!params) return template;
	return template.replace(/\{(\w+)\}/g, (_, name: string) =>
		name in params ? String(params[name]) : `{${name}}`,
	);
}

/** A minimal i18n instance: current locale, translation lookup and change events. */
export interface I18n {
	/** The currently active locale. */
	readonly locale: Locale;
	/**
	 * Translate `key`, interpolating `{placeholder}` tokens from `params`. Falls back
	 * to the default locale, then to the key itself if the message is missing.
	 */
	t(key: MessageKey, params?: Record<string, string | number>): string;
	/** Switch locale; ignored if `locale` is unknown or already active. */
	setLocale(locale: Locale): void;
	/** Subscribe to locale changes; returns an unsubscribe function. */
	onChange(cb: (locale: Locale) => void): () => void;
}

/** Type guard: is `value` one of the supported {@link Locale} codes? */
export function isLocale(value: string | null | undefined): value is Locale {
	return !!value && (LOCALES as readonly string[]).includes(value);
}

/**
 * Create an {@link I18n} runtime.
 *
 * @param initial Starting locale; an unsupported value falls back to the default.
 * @returns A locale-aware translator with change subscriptions.
 */
export function createI18n(initial: Locale = DEFAULT_LOCALE): I18n {
	let locale: Locale = isLocale(initial) ? initial : DEFAULT_LOCALE;
	const subscribers = new Set<(l: Locale) => void>();

	return {
		get locale() {
			return locale;
		},
		t(key, params) {
			const value =
				resolve(DICTIONARIES[locale], key) ?? resolve(DICTIONARIES[DEFAULT_LOCALE], key);
			if (value === undefined) return key; // surface missing keys instead of crashing
			return interpolate(value, params);
		},
		setLocale(next) {
			if (!isLocale(next) || next === locale) return;
			locale = next;
			for (const cb of subscribers) cb(locale);
		},
		onChange(cb) {
			subscribers.add(cb);
			return () => subscribers.delete(cb);
		},
	};
}

export { pl, en, DICTIONARIES, LOCALES, DEFAULT_LOCALE };
export type { Dictionary };
